import React, { useState } from 'react';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/react';

import QuestionPage from '../../../templates/question';
import { CheckboxOptionsWithConfirm } from 'shared-components';

import {
  IFinance,
  IFinanceDocuments,
  IHistory,
  IFundingSourceText,
  ScholarshipSponsorship,
  ICheckbox,
  ScholarshipSponsorshipMap,
} from '../../../../types';
import FundingTable from '../../../atoms/funding-table';
import RemainingFundingTable from '../../../atoms/remaining-funding-table';
import OrganisationSponsors from '../../../organisms/finance/OrganisationSponsor';
import ScholarshipSponsors from '../../../organisms/finance/ScholarhipSponsor';
import BankSponsors from '../../../organisms/finance/BankSponsor';
import EducationSponsors from '../../../organisms/finance/EducationSponsor';
import OtherSponsors from '../../../organisms/finance/OtherSponsor';
import DepositFunding from '../../../organisms/finance/DepositFunding';
import { StudentState } from '../../../../store/student/types';
import { IOffer } from 'api-paths-cas';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

export interface IDispatchProps {
  onSaveDeposit: () => void;
  onCreateSponsor: (region: string, data: any) => void;
  onUpdateSponsor: (region: string, key: string, data: any) => void;
  onRemoveSponsor: (region: string, key: string) => void;
  onRemoveRegion: (region: string) => void;
}

export interface IProps {
  sectionTitle: string;
  complete: boolean;
  currentStep: number;
  steps: number;
  fundingSourcesData: IFundingSourceText[];
  finance: IFinance;
  financeDocuments: IFinanceDocuments;
  offer: IOffer;
  history: IHistory;
  config: StudentState['config'];
  forceShowDeposit: boolean;
  tuitionPostDiscount: boolean;
}

type Props = IProps & IDispatchProps;

const QPage1 = (props: Props) => {
  const getFundingSources = () => {
    const {
      organisationSponsorship = {},
      scholarshipSponsorship = {},
      educationSponsorship = {},
      familySponsorship = {},
      otherSponsorship = {},
    } = props.finance;
    const sources: string[] = [];

    if (Object.keys(organisationSponsorship).length > 0) sources.push('organisationSponsorship');
    if (Object.keys(scholarshipSponsorship).length > 0) sources.push('scholarshipSponsorship');
    if (Object.keys(educationSponsorship).length > 0) sources.push('educationSponsorship');
    if (Object.keys(familySponsorship).length > 0) sources.push('familySponsorship');
    if (Object.keys(otherSponsorship).length > 0) sources.push('otherSponsorship');

    return sources;
  };

  const [fundingSources, setFundingSources] = useState(getFundingSources());

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();
  const onNext = () => navigateToStudentDashboard();

  const getFundingSourcesCheckboxes = (): ICheckbox[] => {
    const checkboxes: ICheckbox[] = [];
    const { scholarshipSponsorship = {} } = props.finance;

    props.fundingSourcesData.forEach(source => {
      checkboxes.push({
        id: source.id,
        label: source.label,
        isTicked: fundingSources.includes(source.id),
        readOnly:
          source.id === 'scholarshipSponsorship' &&
          Object.keys(scholarshipSponsorship).filter(key => {
            // filter dummy scholarship that is added when tuitionPostDiscount is true (we filter in case a rare case of uni switches tuitionPostDiscount to false later on)
            const isDummyScholarship =
              scholarshipSponsorship[key]?.scholarshipName === 'University Scholarship' &&
              scholarshipSponsorship[key]?.courseFundAmount === 0;
            return !isDummyScholarship;
          }).length > 0,
      });
    });

    return checkboxes;
  };

  const handleFundingChange = (fundingId: string, isTicked: boolean) => {
    if (!isTicked) {
      // remove from list
      setFundingSources(prevState => prevState.filter(source => source !== fundingId));
      props.onRemoveRegion(fundingId);
    } else {
      // add to list
      setFundingSources(prevState => prevState.concat([fundingId]));

      if (fundingId === 'scholarshipSponsorship' && props.tuitionPostDiscount) {
        /* If uni is supplying tuition fee post discount fee,
           student is not able to add scholarship sponsors normally.
           To trigger document request, add a dummy scholarship sponsor here.
         */
        const { scholarshipSponsorship = {} } = props.finance;
        const dummySponsor: ScholarshipSponsorship = {
          scholarshipName: 'University Scholarship',
          sponsorshipCovers: [],
          courseFundType: '',
          courseFundPercentage: 0,
          courseFundAmount: 0,
          livingFundType: '',
          livingFundPercentage: 0,
          livingFundAmountType: '',
          livingFundAmount: 0,
          courseAndLivingFundSeparate: false,
          courseAndLivingFundType: '',
          courseAndLivingFundPercentage: 0,
          courseAndLivingFundAmount: 0,
        };

        if (Object.keys(scholarshipSponsorship).length === 0) {
          props.onCreateSponsor('scholarshipSponsorship', dummySponsor);
        }
      }
    }
  };

  const renderSponsors = (fundingSources: string[]) => {
    const visibleSources = props.fundingSourcesData.filter(fundingSourceItem =>
      fundingSources.includes(fundingSourceItem.id),
    );

    const {
      organisationSponsorship = {},
      scholarshipSponsorship = {},
      educationSponsorship = {},
      familySponsorship = {},
      otherSponsorship = {},
      fundingSummary,
    } = props.finance;

    const filteredScholarshipSponsorship: ScholarshipSponsorshipMap = {};
    const activeCoursesLabel = fundingSummary?.courseFees.map(course => course.label) || [];

    Object.keys(scholarshipSponsorship)
      .filter(key => {
        // filter dummy scholarship that is added when tuitionPostDiscount is true (we filter in case a rare case of uni switches tuitionPostDiscount to false later on)
        const isDummyScholarship =
          scholarshipSponsorship[key]?.scholarshipName === 'University Scholarship' &&
          scholarshipSponsorship[key]?.courseFundAmount === 0;
        // filter scholarships of removed courses
        const isActiveScholarship = scholarshipSponsorship[key]?.sponsorshipCovers.find(
          sponsorshipCourseName =>
            activeCoursesLabel?.includes(sponsorshipCourseName) || sponsorshipCourseName === 'studentLivingCost',
        );
        return !isDummyScholarship && isActiveScholarship;
      })
      .forEach(key => {
        filteredScholarshipSponsorship[key] = { ...scholarshipSponsorship[key] };
      });

    const nodes: React.ReactNode[] = visibleSources.map(fundingSourceItem => {
      switch (fundingSourceItem.id) {
        case 'organisationSponsorship':
          return (
            <OrganisationSponsors
              key={fundingSourceItem.id}
              sponsorships={organisationSponsorship}
              fundingSummary={fundingSummary!}
              fundingSourceData={fundingSourceItem}
              config={props.config}
              onCreate={data => {
                props.onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                props.onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                props.onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        case 'scholarshipSponsorship':
          if (props.tuitionPostDiscount && Object.keys(filteredScholarshipSponsorship).length === 0) {
            return null;
          }
          return (
            <ScholarshipSponsors
              key={fundingSourceItem.id}
              sponsorships={filteredScholarshipSponsorship}
              fundingSummary={fundingSummary!}
              fundingSourceData={fundingSourceItem}
              config={props.config}
              disabled={props.tuitionPostDiscount}
              onCreate={data => {
                props.onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                props.onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                props.onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );

        case 'educationSponsorship':
          return (
            <EducationSponsors
              key={fundingSourceItem.id}
              sponsorships={educationSponsorship}
              fundingSummary={fundingSummary!}
              fundingSourceData={fundingSourceItem}
              config={props.config}
              onCreate={data => {
                props.onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                props.onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                props.onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        case 'familySponsorship':
          return (
            <BankSponsors
              key={fundingSourceItem.id}
              sponsorships={familySponsorship}
              fundingSummary={fundingSummary!}
              fundingSourceData={fundingSourceItem}
              config={props.config}
              hasPartner={props.offer.hasPartner ?? false}
              onCreate={data => {
                props.onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                props.onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                props.onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        case 'otherSponsorship':
          return (
            <OtherSponsors
              key={fundingSourceItem.id}
              sponsorships={otherSponsorship}
              fundingSummary={fundingSummary!}
              fundingSourceData={fundingSourceItem}
              config={props.config}
              onCreate={data => {
                props.onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                props.onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                props.onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        default:
          return null;
      }
    });

    return nodes;
  };

  return (
    <QuestionPage
      complete={props.complete}
      steps={props.steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={props.sectionTitle}
    >
      <FundingTable
        fundingSummary={props.finance.fundingSummary!}
        inputHelperText={props.config.Checklist.Finances.Page1.AmountToProve?.helpText}
      />

      <CheckboxOptionsWithConfirm
        id="fundingSources"
        labelText="How are you planning to fund your studies and living expenses?"
        subText="Note: you can tick more than one box"
        inputHelperText={props.config.Checklist.Finances.Page1.FundingSources?.helpText}
        checkboxes={getFundingSourcesCheckboxes()}
        onChange={handleFundingChange}
      />

      {fundingSources.includes('scholarshipSponsorship') && props.tuitionPostDiscount && (
        <Alert status="info" mb={6}>
          <AlertIcon />

          <AlertDescription>
            Please note that you cannot self-declare any scholarships or discounts awarded by the university. If you
            have received a scholarship or discount that is not listed on this page, please contact the university
            directly.
          </AlertDescription>
        </Alert>
      )}

      {renderSponsors(fundingSources)}

      <DepositFunding
        forceShow={props.forceShowDeposit}
        fundingSummary={props.finance.fundingSummary!}
        onSave={props.onSaveDeposit}
      />

      <RemainingFundingTable
        fundingSummary={props.finance.fundingSummary!}
        inputHelperText={props.config.Checklist.Finances.Page1.RemainingAmountToProve?.helpText}
      />
    </QuestionPage>
  );
};

export default QPage1;
